import { ChevronLeft } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface MobileHeaderProps {
	title?: ReactNode;
	subtitle?: ReactNode;
	/** Leading slot, usually a MobileBackButton. */
	left?: ReactNode;
	/** Trailing slot for one or two MobileHeaderButtons. */
	right?: ReactNode;
	/** Shows the title large and left-aligned under the bar, like an iOS root screen. */
	largeTitle?: boolean;
	/** Extra content under the bar: a search field, a segmented control. */
	children?: ReactNode;
	className?: string;
	/** Render on desktop too. By default the header is phone-only (md:hidden). */
	always?: boolean;
}

/**
 * Native-style navigation bar for phone layouts. Pages drop it in at the top of
 * their scroll column; it hides itself at md and up unless `always` is set.
 */
export function MobileHeader({
	title,
	subtitle,
	left,
	right,
	largeTitle = false,
	children,
	className,
	always = false,
}: MobileHeaderProps) {
	return (
		<header
			className={cn(
				'sticky top-0 z-30 shrink-0 border-b border-border/60 bg-background/90 backdrop-blur-xl supports-[backdrop-filter]:bg-background/75',
				!always && 'md:hidden',
				className,
			)}
		>
			{/* Laid out like a UINavigationBar: the title gets the width it needs first and stays
			    centred while both sides have room. The sides never shrink below their buttons (one
			    line, at least 44px), so a long title truncates between them instead of drawing
			    over them, and a back label drops out (MobileBackButton) when it no longer fits. */}
			<div className="grid h-12 grid-cols-[1fr_auto_1fr] items-center gap-1 px-1.5">
				<div className="col-start-1 whitespace-nowrap">
					<div className="flex min-w-11 items-center">{left}</div>
				</div>
				{!largeTitle && (title || subtitle) && (
					<div className="col-start-2 flex min-w-0 flex-col items-center text-center">
						{/* A span title (one that fades out while a large title shows) truncates itself,
						    so its ellipsis fades with it instead of staying behind in the bar. */}
						{title && (
							<div className="w-full truncate text-[17px] font-semibold leading-tight [&>span]:block [&>span]:truncate">
								{title}
							</div>
						)}
						{subtitle && (
							<div className="w-full truncate text-xs leading-tight text-muted-foreground">{subtitle}</div>
						)}
					</div>
				)}
				<div className="col-start-3 whitespace-nowrap">
					<div className="flex min-w-11 items-center justify-end gap-0.5">{right}</div>
				</div>
			</div>
			{largeTitle && title && (
				<div className="px-4 pb-2">
					<h1 className="truncate text-[28px] font-bold leading-tight tracking-tight">{title}</h1>
					{subtitle && <p className="truncate text-sm text-muted-foreground">{subtitle}</p>}
				</div>
			)}
			{children}
		</header>
	);
}

/** 44px round icon button for MobileHeader slots. */
export const MobileHeaderButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
	({ className, type = 'button', ...props }, ref) => (
		<button
			ref={ref}
			type={type}
			className={cn(
				'inline-flex size-11 shrink-0 items-center justify-center rounded-full text-foreground transition-colors active:bg-accent disabled:opacity-40 [&_svg]:size-[22px] [&_svg]:shrink-0',
				className,
			)}
			{...props}
		/>
	),
);
MobileHeaderButton.displayName = 'MobileHeaderButton';

/**
 * Goes back one screen when the previous entry is ours, otherwise to
 * `fallback`. Matches the iOS edge swipe, which also walks history.
 */
export function useMobileBack(fallback: string) {
	const navigate = useNavigate();
	return () => {
		const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
		if (idx > 0) navigate(-1);
		else navigate(fallback, { replace: true });
	};
}

interface MobileBackButtonProps {
	/** Where to go when there is no in-app history to pop. */
	fallback?: string;
	/** Optional text after the chevron, e.g. the parent screen name. */
	label?: ReactNode;
	/** Overrides the default history-based behaviour. */
	onClick?: () => void;
	className?: string;
}

export function MobileBackButton({ fallback = '/inbox', label, onClick, className }: MobileBackButtonProps) {
	const back = useMobileBack(fallback);
	return (
		<button
			type="button"
			onClick={onClick ?? back}
			aria-label={typeof label === 'string' && label !== 'Back' ? `Back to ${label}` : 'Back'}
			className={cn(
				// As in iOS the label shows whole or not at all: when it doesn't fit beside the chevron
				// it wraps onto a second line, which the fixed height clips. It can break anywhere, so
				// it never makes the button's minimum wider than the chevron and the title wins the room.
				'inline-flex h-11 min-w-11 max-w-full flex-wrap content-start items-center overflow-hidden rounded-full pr-2 text-foreground active:opacity-60',
				className,
			)}
		>
			{/* my-2 makes the first line the button's full height, so the chevron stays centred. */}
			<ChevronLeft className="my-2 size-7 shrink-0" strokeWidth={2.25} />
			{label && <span className="-ml-0.5 whitespace-normal break-all text-[17px]">{label}</span>}
		</button>
	);
}
